import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import {
  Gavel,
  Loader2,
  AlertTriangle,
  Clock,
  Ban,
  RefreshCw,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  User,
  Shield,
  History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';

const STATUS_CONFIG = {
  warned: { label: 'Warned', color: 'bg-yellow-100 text-yellow-700', borderColor: 'border-yellow-300', icon: AlertTriangle },
  suspended: { label: 'Suspended', color: 'bg-orange-100 text-orange-700', borderColor: 'border-orange-300', icon: Clock },
  banned: { label: 'Banned', color: 'bg-red-100 text-red-700', borderColor: 'border-red-300', icon: Ban },
};

const formatDate = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

function getTimeRemaining(expiresAt) {
  if (!expiresAt) return null;
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires - now;
  if (diff <= 0) return 'Expired';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function StrikeHistory({ history }) {
  if (!history || history.length === 0) {
    return <p className="text-sm text-gray-400 italic">No strike history</p>;
  }

  return (
    <div className="space-y-2">
      {history.map((strike, i) => (
        <div key={i} className="flex items-start gap-3 text-sm border-l-2 border-gray-200 pl-3 py-1">
          <div className="flex-1">
            <p className="text-gray-700">{strike.reason || 'No reason provided'}</p>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
              <span>{formatDate(strike.date || strike.created_at)}</span>
              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                +{strike.points || 1} point{(strike.points || 1) !== 1 ? 's' : ''}
              </span>
              {strike.source && (
                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                  {strike.source === 'admin_action' ? 'Admin' : strike.source === 'community_auto' ? 'Community' : strike.source === 'dropship_sweep' ? 'Sweep' : strike.source}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDisciplinePage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [, setTick] = useState(0);

  // Tick every 60s to update countdown timers
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/admin/discipline/users?${params}`);
      setUsers(res.data?.users || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Failed to load disciplined users:', err);
      toast.error('Failed to load disciplined users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      console.log('Stats not available');
    }
  }, [api]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);

  const getDisciplineBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.warned;
    const Icon = config.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Gavel className="w-6 h-6 text-red-500" />
              User Discipline
            </h1>
            <p className="text-gray-500">Users currently under progressive discipline</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { fetchUsers(); fetchStats(); }} className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Summary Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-yellow-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-yellow-600 mb-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Warned</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.warned_users || 0}</p>
            </div>
            <div className="bg-white border border-orange-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-orange-600 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Suspended</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.suspended_users || 0}</p>
            </div>
            <div className="bg-white border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <Ban className="w-4 h-4" />
                <span className="text-sm font-medium">Banned</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.banned_users || 0}</p>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[null, 'warned', 'suspended', 'banned'].map((s) => (
            <Button
              key={s || 'all'}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
              className={statusFilter === s ? 'bg-[#1E3A5F]' : ''}
            >
              {s ? STATUS_CONFIG[s].label : 'All'}
              {stats && s && (
                <span className="ml-1 text-xs opacity-70">
                  ({stats[`${s}_users`] || 0})
                </span>
              )}
              {!s && stats && (
                <span className="ml-1 text-xs opacity-70">
                  ({(stats.warned_users || 0) + (stats.suspended_users || 0) + (stats.banned_users || 0)})
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Users List */}
        <div className="space-y-3">
          {users.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No disciplined users</h3>
              <p className="text-gray-500">
                {statusFilter ? `No ${statusFilter} users found` : 'No users are currently under discipline'}
              </p>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                <div className="col-span-3">User</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1 text-center">Strikes</div>
                <div className="col-span-3">Reason</div>
                <div className="col-span-2">Expires</div>
                <div className="col-span-1"></div>
              </div>

              {users.map((user) => {
                const status = user.discipline_status;
                const config = STATUS_CONFIG[status] || STATUS_CONFIG.warned;
                const suspension = user.suspension_status;
                const expiresAt = suspension?.expires_at;
                const timeRemaining = getTimeRemaining(expiresAt);
                const lastStrike = user.strike_history?.[user.strike_history.length - 1];

                return (
                  <div key={user.id} className={`bg-white border ${config.borderColor} rounded-xl overflow-hidden shadow-sm`}>
                    {/* Row */}
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center">
                        {/* User */}
                        <div className="md:col-span-3 flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">@{user.username || 'unknown'}</p>
                            <p className="text-xs text-gray-400 truncate">{user.email || '-'}</p>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="md:col-span-2">
                          {getDisciplineBadge(status)}
                          {suspension?.tier && (
                            <span className="ml-1 text-xs text-gray-400">({suspension.tier})</span>
                          )}
                        </div>

                        {/* Strikes */}
                        <div className="md:col-span-1 text-center">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                            (user.strike_count || 0) >= 7 ? 'bg-red-100 text-red-700' :
                            (user.strike_count || 0) >= 4 ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {user.strike_count || 0}
                          </span>
                        </div>

                        {/* Reason */}
                        <div className="md:col-span-3">
                          <p className="text-sm text-gray-600 truncate">
                            {user.ban_reason || lastStrike?.reason || suspension?.reason || '-'}
                          </p>
                        </div>

                        {/* Expires */}
                        <div className="md:col-span-2">
                          {status === 'suspended' && timeRemaining ? (
                            <span className={`text-sm font-medium ${timeRemaining === 'Expired' ? 'text-green-600' : 'text-orange-600'}`}>
                              {timeRemaining === 'Expired' ? 'Expired' : timeRemaining}
                            </span>
                          ) : status === 'banned' ? (
                            <span className="text-sm text-red-500 font-medium">Permanent</span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>

                        {/* Expand */}
                        <div className="md:col-span-1 flex justify-end">
                          {expandedUser === user.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Strike History */}
                    {expandedUser === user.id && (
                      <div className="border-t border-gray-200 p-4 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          {/* User Info */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                              <User className="w-4 h-4" /> User Details
                            </h4>
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <p className="font-medium text-gray-900">@{user.username || 'unknown'}</p>
                              <p className="text-xs text-gray-500 mt-1">{user.email || '-'}</p>
                              <p className="text-xs text-gray-400 font-mono mt-1">ID: {user.id}</p>
                              <p className="text-xs text-gray-400 mt-1">Joined: {formatDate(user.created_at)}</p>
                              <a
                                href={`/admin/users?highlight=${user.id}`}
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2"
                              >
                                <ExternalLink className="w-3 h-3" /> View Full Profile
                              </a>
                            </div>
                          </div>

                          {/* Discipline Summary */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                              <Shield className="w-4 h-4" /> Discipline Summary
                            </h4>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Total Strikes</span>
                                <span className="font-bold text-gray-900">{user.strike_count || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Status</span>
                                {getDisciplineBadge(status)}
                              </div>
                              {suspension?.tier && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Suspension Tier</span>
                                  <span className="font-medium text-gray-900">{suspension.tier}</span>
                                </div>
                              )}
                              {expiresAt && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Expires</span>
                                  <span className="font-medium text-gray-900">{formatDate(expiresAt)}</span>
                                </div>
                              )}
                              {user.banned_at && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Banned</span>
                                  <span className="font-medium text-gray-900">{formatDate(user.banned_at)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Strike History */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                            <History className="w-4 h-4" /> Strike History
                          </h4>
                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            <StrikeHistory history={user.strike_history} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Total count */}
              {total > users.length && (
                <p className="text-sm text-gray-400 text-center mt-4">
                  Showing {users.length} of {total} users
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
